import {
  reportEvents,
  reportLocation,
  VOYAGE_STATES,
  type VoyageEvent,
  type VoyageSummary,
} from '@felix/domain';
import { ActionIcon, Alert, Button, Skeleton } from '@mantine/core';
import {
  IconCalendarStats,
  IconDownload,
  IconEye,
  IconFileDescription,
  IconGasStation,
  IconListDetails,
  IconRoute,
  IconTimeline,
  IconUnlink,
} from '@tabler/icons-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import table from '../materials/Materials.module.css';
import { browserPdfOutput, pdfErrorKey, renderPdf } from '../personnel/pdf.js';
import { errorCode } from '../personnel/source.js';
import { allReportRows, reportFormPath } from './all-reports.js';
import { plannedFact } from './noon-report.js';
import { useNumber } from './numbers.js';
import { formatDuration } from './overview.js';
import { useReportLabels } from './report-labels.js';
import type { VoyageSource } from './source.js';
import { voyageSummaryPdf, voyageSummaryPdfFileName } from './summary-pdf.js';
import type { Voyage } from './types.js';
import { type FrameData, VOYAGE_TONE, VoyageFrame } from './VoyageFrame.js';
import { LinkSuggestionsPanel, useUnlink } from './VoyageLinksPanels.js';
import classes from './VoyageRecordScreens.module.css';
import { VoyageReferencePanel } from './VoyageReference.js';
import { VoyageTimeline } from './VoyageTimeline.js';
import { voyageSummaryOf } from './voyage-record.js';

const STATUS_TONE: Record<string, StatusTone> = {
  draft: 'warning',
  submitted: 'success',
  amended: 'info',
  reopened: 'warning',
  void: 'danger',
};

export interface VoyageRecordScreenProps {
  source: VoyageSource;
}

/** Üç ekranın başlık kartı olguları: planlanan mesafe, ETD ve ETA (yerel saat + fark). */
function useHeadFacts() {
  const { t } = useTranslation();
  const n = useNumber();
  return (voyage: Voyage) => [
    { label: t('voyage.overview.totalDistance'), value: n.unit(voyage.plannedDistanceNm, 'NM', 0) },
    plannedFact(t('voyage.record.columns.etd'), voyage.plannedEtd, voyage.plannedEtdTzOffsetMin),
    plannedFact(t('voyage.overview.etaUtc'), voyage.plannedEta, voyage.plannedEtaTzOffsetMin),
  ];
}

const twoColumns = (
  <div className={classes.columns}>
    <div className={classes.stack}>
      <Skeleton height={320} radius="md" />
      <Skeleton height={220} radius="md" />
    </div>
    <div className={classes.stack}>
      <Skeleton height={260} radius="md" />
      <Skeleton height={220} radius="md" />
    </div>
  </div>
);

/** Seferin özeti ve olayları; iptal edilen rapor girmez (ADR 0077). */
function useRecord(data: FrameData): { summary: VoyageSummary; events: VoyageEvent[] } {
  return useMemo(
    () => ({
      summary: voyageSummaryOf(data.voyage, data.reports),
      events: reportEvents(data.reports),
    }),
    [data.voyage, data.reports],
  );
}

/**
 * Voyage ▸ Voyage Reports ▸ Voyage Details (ADR 0082 Ek §2; ortağın `sefer-3.jpeg` "Voyage
 * Record"): sefer kaydı ve bağlı raporlar tarih sırasıyla; olaylar, bacak mesafeleri, yakıt
 * tüketimi ve sefer dönemleri ayrı kartlarda. Hepsi raporlardan türetilir (S12).
 */
export function VoyageDetailsScreen({ source }: VoyageRecordScreenProps) {
  const { t } = useTranslation();
  const facts = useHeadFacts();
  const title = t('voyage.record.details.title');
  return (
    <VoyageFrame
      source={source}
      title={title}
      description={t('voyage.record.details.description')}
      facts={facts}
      skeleton={twoColumns}
    >
      {(data) => <VoyageDetailsView data={data} source={source} />}
    </VoyageFrame>
  );
}

/** Voyage Details'in gövdesi (veri dışarıdan; test edilebilir). */
export function VoyageDetailsView({ data, source }: { data: FrameData; source: VoyageSource }) {
  const { t } = useTranslation();
  const shell = useShell();
  const n = useNumber();
  const labels = useReportLabels();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.record.details.${key}`, options);
  const c = (key: string) => t(`voyage.record.columns.${key}`);
  const { voyage, reports, voyages } = data;
  const { summary, events } = useRecord(data);
  const rows = useMemo(
    () =>
      allReportRows(reports, voyages).sort(
        (a, b) => Date.parse(a.report.reportedAt) - Date.parse(b.report.reportedAt),
      ),
    [reports, voyages],
  );
  const vesselQuery = source.scope === 'web' && data.vessel ? `&vessel=${data.vessel.id}` : '';
  const go = (path: string) => shell?.navigate(path);
  const first = summary.start;
  // Bağlar (ADR 0083 §2, §3): öneriler ve bağlı raporun çözülmesi; gemide doğrudan, ofiste talep
  const changed = () => data.reload?.();
  const unlinking = useUnlink(source, voyage, changed);

  return (
    <div className={classes.columns}>
      <div className={classes.stack}>
        <LinkSuggestionsPanel
          key={`${voyage.id}-${reports.length}`}
          source={source}
          voyage={voyage}
          voyages={voyages}
          onChanged={changed}
        />
        <Panel
          title={k('linkedReports')}
          icon={<IconFileDescription size={20} stroke={1.8} />}
          flush
        >
          {rows.length === 0 ? (
            <EmptyState
              title={k('noReports')}
              description={source.editor ? k('noReportsShip') : k('noReportsWeb')}
            />
          ) : (
            <TableBox label={k('linkedReports')}>
              <table className={`${table.table} ${classes.table}`}>
                <thead>
                  <tr>
                    <th scope="col" className={classes.colNo}>
                      #
                    </th>
                    <th scope="col" className={classes.colDate}>
                      {c('date')}
                    </th>
                    <th scope="col">{c('report')}</th>
                    <th scope="col">{c('location')}</th>
                    <th scope="col">{t('voyage.allReports.columns.keyInfo')}</th>
                    <th scope="col" className={classes.colStatus}>
                      {t('voyage.allReports.columns.status')}
                    </th>
                    <th scope="col" className={classes.colAction}>
                      <span className={classes.srOnly}>
                        {t('voyage.allReports.columns.actions')}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const type = labels.typeLabel(row.report.kind, row.type);
                    const info = labels.keyInfo(row.keyInfo);
                    return (
                      <tr key={row.report.id}>
                        <td className={classes.colNo}>{index + 1}</td>
                        <td>{formatDateTime(row.report.reportedAt, 0)}</td>
                        <td className={classes.ellipsis} title={type}>
                          {type}
                        </td>
                        <td className={classes.ellipsis} title={labels.locationLabel(row.location)}>
                          {labels.locationLabel(row.location)}
                        </td>
                        <td className={classes.ellipsis} title={info || undefined}>
                          {info || '—'}
                        </td>
                        <td>
                          <StatusBadge tone={STATUS_TONE[row.status] ?? 'neutral'}>
                            {labels.statusLabel(row.status)}
                          </StatusBadge>
                        </td>
                        <td className={classes.action}>
                          {row.report.linkId && unlinking.writable ? (
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              loading={unlinking.busy === row.report.id}
                              aria-label={t('voyage.links.linked.unlinkTitle', {
                                no: row.report.reportNo || type,
                              })}
                              title={
                                unlinking.office
                                  ? t('voyage.links.linked.requestUnlink')
                                  : t('voyage.links.linked.unlink')
                              }
                              onClick={() => void unlinking.run(row.report)}
                            >
                              <IconUnlink size={18} />
                            </ActionIcon>
                          ) : null}
                          <ActionIcon
                            variant="subtle"
                            aria-label={t('voyage.allReports.view', {
                              no: row.report.reportNo || type,
                            })}
                            title={t('voyage.allReports.viewTitle')}
                            onClick={() =>
                              go(
                                reportFormPath(
                                  row,
                                  source.scope === 'web' ? (data.vessel?.id ?? null) : null,
                                ),
                              )
                            }
                          >
                            <IconEye size={18} />
                          </ActionIcon>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableBox>
          )}
          {unlinking.message ? (
            <p
              className={classes.note}
              role={unlinking.message.tone === 'danger' ? 'alert' : 'status'}
            >
              {unlinking.message.text}
            </p>
          ) : null}
        </Panel>
        <LegsPanel summary={summary} />
        <PeriodsPanel summary={summary} />
      </div>

      <div className={classes.stack}>
        <Panel
          title={k('record')}
          icon={<IconListDetails size={20} stroke={1.8} />}
          extra={<StatusBadge tone="success">{k('linked')}</StatusBadge>}
        >
          <dl className={classes.facts}>
            <dt>{c('voyageNo')}</dt>
            <dd>{voyage.voyageNo}</dd>
            <dt>{c('departurePort')}</dt>
            <dd>{voyage.fromPort}</dd>
            <dt>{c('arrivalPort')}</dt>
            <dd>{voyage.toPort}</dd>
            <dt>{t('voyage.allReports.columns.status')}</dt>
            <dd>
              <StatusBadge tone={VOYAGE_TONE[voyage.status]}>
                {t(`voyage.voyageStatus.${voyage.status}`)}
              </StatusBadge>
            </dd>
            <dt>{c('start')}</dt>
            <dd>{formatDateTime(voyage.start ?? first, 0)}</dd>
            <dt>{c('end')}</dt>
            <dd>{voyage.end ? formatDateTime(voyage.end, 0) : t('voyage.record.ongoing')}</dd>
            <dt>{k('legs')}</dt>
            <dd>{summary.legs.length}</dd>
            <dt>{k('linkedReports')}</dt>
            <dd>{reports.length}</dd>
            <dt>{k('distance')}</dt>
            <dd>
              {n.unit(summary.distanceNm, 'nm', 0)} / {n.unit(voyage.plannedDistanceNm, 'nm', 0)}
            </dd>
            {voyage.charterer ? (
              <>
                <dt>{t('voyage.event.fields.charterPartyNo')}</dt>
                <dd>{[voyage.charterer, voyage.charterPartyNo].filter(Boolean).join(' · ')}</dd>
              </>
            ) : null}
          </dl>
          <div className={classes.links}>
            <Button
              variant="default"
              size="xs"
              leftSection={<IconTimeline size={14} />}
              onClick={() => go(`/voyage/timeline?voyage=${voyage.id}${vesselQuery}`)}
            >
              {t('menu.voyageTimeline')}
            </Button>
            <Button
              variant="default"
              size="xs"
              leftSection={<IconCalendarStats size={14} />}
              onClick={() => go(`/voyage/summary?voyage=${voyage.id}${vesselQuery}`)}
            >
              {t('menu.voyageSummary')}
            </Button>
          </div>
        </Panel>

        {/* Seferin CP'si ve performans referansı, salt okunur (ADR 0084 Ek 2) */}
        <VoyageReferencePanel
          source={source}
          voyage={voyage}
          reference={data.performance?.reference}
        />
        <EventsPanel events={events} reports={reports} />
        <FuelPanel summary={summary} />
      </div>
    </div>
  );
}

function EventsPanel({
  events,
  reports,
}: {
  events: readonly VoyageEvent[];
  reports: FrameData['reports'];
}) {
  const { t } = useTranslation();
  const labels = useReportLabels();
  const k = (key: string) => t(`voyage.record.details.${key}`);
  const c = (key: string) => t(`voyage.record.columns.${key}`);
  const byId = useMemo(() => new Map(reports.map((r) => [r.id, r])), [reports]);
  return (
    <Panel title={k('events')} icon={<IconTimeline size={20} stroke={1.8} />} flush>
      {events.length === 0 ? (
        <p className={classes.empty}>{k('noEvents')}</p>
      ) : (
        <TableBox label={k('events')}>
          <table className={`${table.table} ${classes.table}`}>
            <thead>
              <tr>
                <th scope="col" className={classes.colDate}>
                  {c('date')}
                </th>
                <th scope="col">{c('event')}</th>
                <th scope="col">{c('report')}</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => {
                const report = byId.get(event.reportId);
                const source = report
                  ? `${labels.typeLabel(report.kind)} · ${labels.locationLabel(reportLocation(report))}`
                  : '—';
                return (
                  <tr key={event.key}>
                    <td>{formatDateTime(event.at, 0)}</td>
                    <td>
                      <span className={classes.event} data-category={event.category}>
                        {t(`voyage.record.eventTypes.${event.type}`)}
                      </span>
                    </td>
                    <td className={classes.ellipsis} title={source}>
                      {source}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableBox>
      )}
    </Panel>
  );
}

function TableBox({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section
      className={`${table.tableWrap} ${classes.tableBox}`}
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      {children}
    </section>
  );
}

function LegsPanel({ summary }: { summary: VoyageSummary }) {
  const { t } = useTranslation();
  const n = useNumber();
  const c = (key: string) => t(`voyage.record.columns.${key}`);
  const title = t('voyage.record.details.legs');
  return (
    <Panel title={title} icon={<IconRoute size={20} stroke={1.8} />} flush>
      {summary.legs.length === 0 ? (
        <p className={classes.empty}>{t('voyage.record.details.noLegs')}</p>
      ) : (
        <TableBox label={title}>
          <table className={`${table.table} ${classes.table}`}>
            <thead>
              <tr>
                <th scope="col" className={classes.colNo}>
                  {c('leg')}
                </th>
                <th scope="col">{c('from')}</th>
                <th scope="col">{c('to')}</th>
                <th scope="col" className={classes.colDate}>
                  {c('start')}
                </th>
                <th scope="col" className={classes.colDate}>
                  {c('end')}
                </th>
                <th scope="col" className={classes.num}>
                  {c('distance')}
                </th>
                <th scope="col" className={classes.num}>
                  {c('duration')}
                </th>
                <th scope="col" className={classes.num}>
                  {c('avgSpeed')}
                </th>
              </tr>
            </thead>
            <tbody>
              {summary.legs.map((leg) => (
                <tr key={leg.no}>
                  <td className={classes.colNo}>{leg.no}</td>
                  <td className={classes.ellipsis} title={leg.from ?? undefined}>
                    {leg.from ?? '—'}
                  </td>
                  <td className={classes.ellipsis} title={leg.to ?? undefined}>
                    {leg.to ?? '—'}
                  </td>
                  <td>{formatDateTime(leg.start, 0)}</td>
                  <td>{formatDateTime(leg.end, 0)}</td>
                  <td className={classes.num}>{n(leg.distanceNm, 0)}</td>
                  <td className={classes.num}>{formatDuration(leg.hours)}</td>
                  <td className={classes.num}>{n(leg.avgSpeedKn, { digits: 1, fixed: true })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
      )}
    </Panel>
  );
}

function PeriodsPanel({ summary }: { summary: VoyageSummary }) {
  const { t } = useTranslation();
  const n = useNumber();
  const c = (key: string) => t(`voyage.record.columns.${key}`);
  const title = t('voyage.record.details.periods');
  return (
    <Panel title={title} icon={<IconCalendarStats size={20} stroke={1.8} />} flush>
      {summary.periods.length === 0 ? (
        <p className={classes.empty}>{t('voyage.record.details.noPeriods')}</p>
      ) : (
        <TableBox label={title}>
          <table className={`${table.table} ${classes.table}`}>
            <thead>
              <tr>
                <th scope="col">{c('period')}</th>
                <th scope="col">{c('place')}</th>
                <th scope="col" className={classes.colDate}>
                  {c('start')}
                </th>
                <th scope="col" className={classes.colDate}>
                  {c('end')}
                </th>
                <th scope="col" className={classes.num}>
                  {c('distance')}
                </th>
                <th scope="col" className={classes.num}>
                  {c('duration')}
                </th>
              </tr>
            </thead>
            <tbody>
              {summary.periods.map((period) => (
                <tr key={period.start}>
                  <td>
                    <span className={classes.state} data-state={period.state}>
                      {t(`voyage.record.states.${period.state}`)}
                    </span>
                  </td>
                  <td className={classes.ellipsis} title={period.place ?? undefined}>
                    {period.place ?? '—'}
                  </td>
                  <td>{formatDateTime(period.start, 0)}</td>
                  <td>{formatDateTime(period.end, 0)}</td>
                  <td className={classes.num}>{n(period.distanceNm, 0)}</td>
                  <td className={classes.num}>{formatDuration(period.hours)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
      )}
    </Panel>
  );
}

function FuelPanel({ summary }: { summary: VoyageSummary }) {
  const { t } = useTranslation();
  const n = useNumber();
  const c = (key: string) => t(`voyage.record.columns.${key}`);
  const title = t('voyage.record.details.fuel');
  const two = { digits: 2, fixed: true } as const;
  return (
    <Panel title={title} icon={<IconGasStation size={20} stroke={1.8} />} flush>
      {summary.fuels.length === 0 ? (
        <p className={classes.empty}>{t('voyage.record.summary.noFuel')}</p>
      ) : (
        <TableBox label={title}>
          <table className={`${table.table} ${classes.table}`}>
            <thead>
              <tr>
                <th scope="col">{c('fuel')}</th>
                {VOYAGE_STATES.map((state) => (
                  <th key={state} scope="col" className={classes.num}>
                    {t(`voyage.record.states.${state}`)}
                  </th>
                ))}
                <th scope="col" className={classes.num}>
                  {c('totalMt')}
                </th>
              </tr>
            </thead>
            <tbody>
              {summary.fuels.map((row) => (
                <tr key={row.fuel}>
                  <th scope="row">{row.fuel}</th>
                  {VOYAGE_STATES.map((state) => (
                    <td key={state} className={classes.num}>
                      {n(row.byState[state], two)}
                    </td>
                  ))}
                  <td className={`${classes.num} ${classes.strong}`}>{n(row.total, two)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
      )}
    </Panel>
  );
}

/**
 * Voyage ▸ Voyage Reports ▸ Voyage Summary (ADR 0082 Ek §2; ortağın `sefer-3.jpeg`, S9, S11):
 * seyir, liman, demir, sürüklenme ve bekleme süreleri, mesafe, hız ve yakıt türüne göre tüketim;
 * dönemler ve bacaklar. Tamamlanan seferde "Final", sürende "Provisional"; PDF aynı türetmeden.
 */
export function VoyageSummaryScreen({ source }: VoyageRecordScreenProps) {
  const { t } = useTranslation();
  const facts = useHeadFacts();
  return (
    <VoyageFrame
      source={source}
      title={t('voyage.record.summary.title')}
      description={t('voyage.record.summary.description')}
      facts={facts}
      action={(data) => <SummaryPdfButton data={data} source={source} />}
      skeleton={
        <div className={classes.stack}>
          <Skeleton height={120} radius="md" />
          {twoColumns}
        </div>
      }
    >
      {(data) => <VoyageSummaryView data={data} />}
    </VoyageFrame>
  );
}

function SummaryPdfButton({ data, source }: { data: FrameData; source: VoyageSource }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { summary } = useRecord(data);
  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const brand = (await source.brand?.().catch(() => null)) ?? null;
      const tr = (key: string, options?: Record<string, unknown>) => String(t(key, options ?? {}));
      const definition = voyageSummaryPdf(tr, {
        voyage: data.voyage,
        summary,
        vessel: { name: data.vessel?.name ?? '', imo: data.vessel?.imo ?? null },
        brand,
        printedAt: new Date().toISOString(),
      });
      const output = source.pdf ?? browserPdfOutput();
      await output.save(
        voyageSummaryPdfFileName(data.voyage.voyageNo, t('voyage.record.summary.title')),
        () => renderPdf(definition),
      );
    } catch (failure) {
      const key = pdfErrorKey(errorCode(failure));
      setError(key === 'restHours.pdf.error' ? t('voyage.pdf.error') : t(key));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={classes.pdf}>
      <Button
        leftSection={<IconDownload size={16} />}
        loading={busy}
        disabled={summary.start === null}
        onClick={() => void download()}
      >
        {t('voyage.record.summary.pdf')}
      </Button>
      {error ? (
        <Alert color="red" variant="light" role="alert" className={classes.pdfError}>
          {error}
        </Alert>
      ) : null}
    </div>
  );
}

/** Voyage Summary'nin gövdesi (veri dışarıdan; test edilebilir). */
export function VoyageSummaryView({ data }: { data: FrameData }) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.record.summary.${key}`, options);
  const { summary } = useRecord(data);
  const final = data.voyage.status === 'completed';
  if (summary.start === null) {
    return (
      <Panel>
        <EmptyState title={k('noDataTitle')} description={k('noData')} />
      </Panel>
    );
  }
  const total = summary.hours.total;
  const tiles: { key: string; label: string; value: string; sub?: string; state?: string }[] = [
    {
      key: 'sailing',
      label: k('sailing'),
      value: formatDuration(summary.hours.sea),
      state: 'sea',
      ...(summary.hours.passage > 0
        ? { sub: k('passage', { value: formatDuration(summary.hours.passage) }) }
        : {}),
    },
    { key: 'port', label: k('port'), value: formatDuration(summary.hours.port), state: 'port' },
    {
      key: 'anchorage',
      label: k('anchorage'),
      value: formatDuration(summary.hours.anchorage),
      state: 'anchorage',
    },
    { key: 'drift', label: k('drift'), value: formatDuration(summary.hours.drift), state: 'drift' },
    {
      key: 'waiting',
      label: k('waiting'),
      value: formatDuration(summary.hours.waiting),
      state: 'waiting',
    },
    { key: 'distance', label: k('distance'), value: n.unit(summary.distanceNm, 'nm', 0) },
    {
      key: 'speed',
      label: k('avgSpeed'),
      value: n.unit(summary.avgSpeedKn, 'kn', { digits: 1, fixed: true }),
    },
    {
      key: 'consumption',
      label: k('consumption'),
      value: n.unit(summary.consumptionMt, 'MT', { digits: 1, fixed: true }),
    },
  ];
  return (
    <div className={classes.stack}>
      <Panel
        title={k('figures')}
        icon={<IconCalendarStats size={20} stroke={1.8} />}
        extra={
          <StatusBadge tone={final ? 'success' : 'warning'}>
            {final ? k('final') : k('provisional')}
          </StatusBadge>
        }
      >
        <p className={classes.note}>{final ? k('finalNote') : k('provisionalNote')}</p>
        <ul className={classes.tiles}>
          {tiles.map((tile) => (
            <li key={tile.key} className={classes.tile} data-state={tile.state}>
              <span className={classes.tileLabel}>{tile.label}</span>
              <strong className={classes.tileValue}>{tile.value}</strong>
              {tile.sub ? <span className={classes.tileSub}>{tile.sub}</span> : null}
            </li>
          ))}
        </ul>
        <div className={classes.timeBar}>
          <span className={classes.timeTitle}>{k('time')}</span>
          <div className={classes.bar} role="img" aria-label={k('time')}>
            {VOYAGE_STATES.filter((state) => summary.hours[state] > 0).map((state) => (
              <span
                key={state}
                className={classes.barPart}
                data-state={state}
                style={{ width: `${(summary.hours[state] / total) * 100}%` }}
                title={`${t(`voyage.record.states.${state}`)} · ${formatDuration(summary.hours[state])}`}
              />
            ))}
          </div>
          <ul className={classes.barLegend}>
            {VOYAGE_STATES.map((state) => (
              <li key={state}>
                <span className={classes.legendDot} data-state={state} aria-hidden />
                {t(`voyage.record.states.${state}`)}{' '}
                <strong>{total > 0 ? Math.round((summary.hours[state] / total) * 100) : 0}%</strong>
              </li>
            ))}
          </ul>
        </div>
      </Panel>
      <FuelPanel summary={summary} />
      <LegsPanel summary={summary} />
      <PeriodsPanel summary={summary} />
    </div>
  );
}

/**
 * Voyage ▸ Voyage Reports ▸ Voyage Timeline (ADR 0082 Ek §3; ortağın `sefer-6.jpeg` alt bölümü):
 * seçilen seferin yatay zaman çizelgesi ve altında olay listesi.
 */
export function VoyageTimelineScreen({ source }: VoyageRecordScreenProps) {
  const { t } = useTranslation();
  const facts = useHeadFacts();
  return (
    <VoyageFrame
      source={source}
      title={t('voyage.record.timeline.title')}
      description={t('voyage.record.timeline.description')}
      facts={facts}
      skeleton={<Skeleton height={260} radius="md" />}
    >
      {(data) => <TimelineBody data={data} />}
    </VoyageFrame>
  );
}

function TimelineBody({ data }: { data: FrameData }) {
  const { summary, events } = useRecord(data);
  const { voyage } = data;
  return (
    <div className={classes.stack}>
      <VoyageTimeline
        summary={summary}
        events={events}
        reports={data.reports}
        eta={voyage.status === 'completed' ? null : { at: voyage.plannedEta, port: voyage.toPort }}
      />
      <EventsPanel events={events} reports={data.reports} />
    </div>
  );
}
